import { fondHasStrategy } from '../../common/forms/FondForm/fonds';
import { InvestingExperienceOptions } from '../../common/forms/InvestingExperience';
import {
  ConsultancyInitiationFormValues,
  FinancesFormValues,
  financesValidationSchema,
  HazardProfileFormValues,
  InvestingExperienceCalcSchema,
  InvestingExperienceFormValues,
} from '../../common/models/Forms';
import { InvestingStrategy, RPs, strategiesMap } from './constants';

interface InvestingState {
  experience: InvestingExperienceFormValues | null;
  hazard: HazardProfileFormValues | null;
  finances: FinancesFormValues | null;
}

export function calculateStrategy({ finances, hazard, experience }: InvestingState): InvestingStrategy {
  if (!experience || !hazard || !finances) {
    // If cant calculate strategy return most conservative
    return InvestingStrategy.Conservative;
  }
  let pointsFromTable = (experience?.table ?? []).reduce((a, c) => {
    const rule = (InvestingExperienceCalcSchema.questions as Record<string, Record<string, number>>)[c.question];
    if (rule?.[c.value]) {
      a += rule[c.value];
    }
    return a;
  }, 0);

  if (
    (experience?.table ?? []).some(
      (entry) => entry.question === 'a' && entry.value !== InvestingExperienceOptions.NoKnowledge,
    )
  ) {
    const aPoints = (InvestingExperienceCalcSchema.sub.a as Record<string, number>)[experience?.a ?? ''];
    pointsFromTable += aPoints;
  }

  if (
    (experience?.table ?? []).some(
      (entry) =>
        (entry.question === 'b' || entry.question === 'c') && entry.value !== InvestingExperienceOptions.NoKnowledge,
    )
  ) {
    const bPoints = (InvestingExperienceCalcSchema.sub.b as Record<string, number>)[experience?.b?.a ?? ''];
    pointsFromTable += bPoints;
  }

  if (
    (experience?.table ?? []).some(
      (entry) =>
        (entry.question === 'd' || entry.question === 'e') && entry.value !== InvestingExperienceOptions.NoKnowledge,
    )
  ) {
    const cPoints = (InvestingExperienceCalcSchema.sub.c as Record<string, number>)[experience?.c?.a ?? ''];
    pointsFromTable += cPoints;
  }

  const wayOfEducationPoints = (InvestingExperienceCalcSchema.wayOfEducation as Record<string, number>)[
    experience?.wayOfEducation ?? ''
  ];

  if (wayOfEducationPoints) {
    pointsFromTable += wayOfEducationPoints;
  }

  const useOfServicesPoints = (InvestingExperienceCalcSchema.useOfServices as Record<string, number>)[
    experience?.useOfServices ?? ''
  ];

  if (useOfServicesPoints) {
    pointsFromTable += useOfServicesPoints;
  }

  const investingStrategy = getStrategyFromInvesting(pointsFromTable);
  const hazardStrategy = getStrategyFromHazard(hazard);
  const financesStrategy = getStrategyFromFinances(finances);

  const strategies = [investingStrategy, hazardStrategy, financesStrategy];

  const mappedStrategies = strategies.map((s) => strategiesMap.find((e) => e.type === s));

  const result = Math.min(...mappedStrategies.map((s) => s!.value));

  return strategiesMap[result - 1].type;
}

export function getStrategyFromInvesting(value: number) {
  if (value >= 0 && value <= 3) {
    return InvestingStrategy.Conservative;
  }
  if (value >= 4 && value <= 8) {
    return InvestingStrategy.Defensive;
  }
  if (value >= 9 && value <= 13) {
    return InvestingStrategy.Balanced;
  }
  return InvestingStrategy.Dynamic;
}

export function getStrategyFromFinances(values: FinancesFormValues) {
  const points = Object.entries(values).reduce((a, [key, value]) => {
    const rule = (financesValidationSchema as Record<string, Record<string, number>>)[key];
    if (rule?.[value]) {
      a += rule[value];
    }
    return a;
  }, 0);

  if (points >= 0 && points <= 1) {
    return InvestingStrategy.Conservative;
  }
  if (points >= 2 && points <= 3) {
    return InvestingStrategy.Defensive;
  }
  if (points >= 4 && points <= 6) {
    return InvestingStrategy.Balanced;
  }
  return InvestingStrategy.Dynamic;
}

export function getStrategyFromHazard(values: HazardProfileFormValues) {
  function fromA(value: string) {
    switch (value) {
      case 'a':
        return RPs.RP1;
      case 'b':
        return RPs.RP2;
      case 'c':
        return RPs.RP3;
      case 'd':
        return RPs.RP4;
      default:
        return RPs.RP1;
    }
  }

  function fromD(value: string) {
    switch (value) {
      case 'a':
        return RPs.RP1;
      case 'b':
        return RPs.RP2;
      case 'c':
        return RPs.RP3;
      case 'd':
        return RPs.RP3;
      case 'e':
        return RPs.RP4;
      default:
        return RPs.RP1;
    }
  }

  function fromC(value: string) {
    switch (value) {
      case 'a':
        return RPs.RP1;
      case 'b':
        return RPs.RP1;
      case 'c':
        return RPs.RP2;
      case 'd':
        return RPs.RP3;
      case 'e':
        return RPs.RP3;
      case 'f':
        return RPs.RP4;
      case 'g':
        return RPs.RP4;
      default:
        return RPs.RP1;
    }
  }

  const aRP = fromA(values.a);
  const bRP = fromA(values.b);
  const cRP = fromC(values.c);
  const dRP = fromD(values.d);

  const result = Math.min(aRP, bRP, cRP, dRP);

  switch (result) {
    case RPs.RP1:
      return InvestingStrategy.Conservative;
    case RPs.RP2:
      return InvestingStrategy.Defensive;
    case RPs.RP3:
      return InvestingStrategy.Balanced;
    case RPs.RP4:
      return InvestingStrategy.Dynamic;
    default:
      return InvestingStrategy.Conservative;
  }
}

export function updateProtocolInitiationByStrategy(
  state: ConsultancyInitiationFormValues | null,
  strategy: InvestingStrategy,
) {
  if (!state?.table) {
    return state;
  }

  const table = state.table.filter((item) => fondHasStrategy(item.code, strategy));

  return { ...state, table };
}
